import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon, DownloadIcon, ShowcaseIcon, BroomIcon } from './icons';
import { useAuth } from '../contexts/AuthContext';
import { addToShowcase, removeFromShowcase } from '../services/firebase';
import { invalidateGalleryCache, loadUserGallery } from '../utils/galleryCache';

interface GalleryImage {
  id: string;
  originalImage: string;
  generatedImage: string;
  createdAt: Date;
  prompt?: string;
  background?: string;
  outfit?: string;
  pose?: string;
  settings?: any;
  showcaseId?: string; // ショーケースに追加されている場合のID
}

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// プロンプトから重要な部分（背景・服装・顔の向き）を抽出する関数
const extractImportantPromptParts = (prompt: string): string => {
  const parts: string[] = [];
  
  console.log('🔍 プロンプト抽出開始:', {
    promptLength: prompt.length,
    promptPreview: prompt.substring(0, 200) + '...'
  });
  
  // 背景の指定を探す（複数の形式に対応）
  const backgroundPatterns = [
    // 「背景:」の形式（現在のプロンプト生成で使用）
    { pattern: /背景[：:]\s*([^。、]+)/, type: 'current' },
    // 「背景は:」の形式（旧形式）
    { pattern: /背景は[：:]\s*([^。、]+)/, type: 'custom' },
    // テンプレートプロンプトの形式
    { pattern: /プロフェッショナルな([^、]+)のスタジオ背景/, type: 'studio' },
    { pattern: /([^、]+)の内装を背景に/, type: 'interior' },
    { pattern: /([^、]+)を背景に/, type: 'general' },
    { pattern: /([^、]+)の舞台を背景に/, type: 'stage' }
  ];
  
  let backgroundFound = false;
  for (const bgInfo of backgroundPatterns) {
    const match = prompt.match(bgInfo.pattern);
    if (match) {
      const background = match[1].trim();
      if (background && background !== '') {
        let displayBackground = background;
        
        // 背景名を簡潔に変換
        if (bgInfo.type === 'current' || bgInfo.type === 'studio') {
          if (background.includes('ダークグレーのスタジオ')) {
            displayBackground = 'スタジオ（ダークグレー）';
          } else if (background.includes('グレーのスタジオ')) {
            displayBackground = 'スタジオ（グレー）';
          } else if (background.includes('白いスタジオ')) {
            displayBackground = 'スタジオ（ホワイト）';
          } else if (background.includes('ライトパープルのスタジオ')) {
            displayBackground = 'スタジオ（ライトパープル）';
          } else if (background.includes('ライトピンクのスタジオ')) {
            displayBackground = 'スタジオ（ライトピンク）';
          } else if (background.includes('ライトブルーのスタジオ')) {
            displayBackground = 'スタジオ（ライトブルー）';
          } else if (background.includes('白い木目')) {
            displayBackground = '白い木目';
          } else if (background.includes('美容室風')) {
            displayBackground = '美容室風';
          } else if (background.includes('カフェ風')) {
            displayBackground = 'カフェ風';
          } else if (background.includes('コンサートホール風')) {
            displayBackground = 'コンサートホール風';
          } else if (background.includes('ビーチ風')) {
            displayBackground = 'ビーチ風';
          } else if (background.includes('都会風')) {
            displayBackground = '都会風';
          } else if (background.includes('プロフェッショナルなスタジオ')) {
            displayBackground = 'プロフェッショナルなスタジオ';
          } else {
            displayBackground = background;
          }
        } else if (bgInfo.type === 'interior') {
          if (background.includes('美容室')) {
            displayBackground = '美容室';
          } else if (background.includes('カフェ')) {
            displayBackground = '落ち着いたカフェ';
          }
        } else if (bgInfo.type === 'stage') {
          if (background.includes('コンサートホール')) {
            displayBackground = 'コンサートホール';
          }
        } else if (bgInfo.type === 'general') {
          if (background.includes('ビーチ') && background.includes('夕焼け')) {
            displayBackground = 'ビーチ・夕焼け';
          } else if (background.includes('都会') && background.includes('街並み')) {
            displayBackground = '都会の街並み';
          }
        }
        
        parts.push(`背景: ${displayBackground}`);
        backgroundFound = true;
        console.log('✅ 背景抽出成功:', {
          originalBackground: background,
          displayBackground,
          type: bgInfo.type
        });
        break;
      }
    }
  }
  
  // 背景が見つからない場合は、プロンプトから「背景:」と「背景は:」部分を除外して処理を続行
  const promptWithoutBackground = backgroundFound ? prompt : prompt.replace(/背景[：:]\s*[^。、]+/g, '').replace(/背景は[：:]\s*[^。、]+/g, '');
  
  // 服装の指定を探す（「衣装:」と「衣装は:」の形式で追加される）
  const outfitMatch = promptWithoutBackground.match(/衣装[：:]\s*([^。、|]+)/) || promptWithoutBackground.match(/衣装は[：:]\s*([^。、|]+)/);
  if (outfitMatch) {
    let outfit = outfitMatch[1].trim();
    // 服装名に「背景:」が含まれている場合は削除
    outfit = outfit.replace(/背景[：:]\s*[^。、|]+/g, '').replace(/背景は[：:]\s*[^。、|]+/g, '').trim();
    // 服装名にポーズの説明が含まれている場合は削除
    outfit = outfit.replace(/背筋を伸ばし[^。、|]*/g, '').replace(/あごを軽く引き[^。、|]*/g, '').trim();
    
    if (outfit && outfit !== '') {
      // 服装の説明を簡潔に
      let shortOutfit = outfit;
      
      // カスタムテキストの判定（長いテキストや特殊な色・スタイルを含む場合）
      const isCustomText = outfit.length > 15 || 
        outfit.includes('オレンジ') || outfit.includes('ノースリーブ') || 
        outfit.includes('カスタム') || outfit.includes('その他') ||
        outfit.includes('レッド') || outfit.includes('ピンク') || 
        outfit.includes('イエロー') || outfit.includes('パープル') ||
        outfit.includes('グリーン') || outfit.includes('ブルー') ||
        outfit.includes('ロング') || outfit.includes('ショート') ||
        outfit.includes('袖なし') || outfit.includes('半袖');
      
      if (isCustomText) {
        shortOutfit = outfit; // カスタムテキストはそのまま表示
      } else if (outfit.includes('ネイビーのビジネススーツ') || outfit.includes('ネイビーブルー')) {
        shortOutfit = 'ビジネススーツ (ネイビー)';
      } else if (outfit.includes('ブラックのビジネススーツ') || (outfit.includes('ブラック') && outfit.includes('ビジネス'))) {
        shortOutfit = 'ビジネススーツ (ブラック)';
      } else if (outfit.includes('グレーのビジネススーツ')) {
        shortOutfit = 'ビジネススーツ (グレー)';
      } else if (outfit.includes('白いTシャツ') || outfit.includes('白いクルーネックTシャツ')) {
        shortOutfit = 'カジュアルTシャツ (白)';
      } else if (outfit.includes('ブラックのTシャツ')) {
        shortOutfit = 'カジュアルTシャツ (黒)';
      } else if (outfit.includes('白いブラウス')) {
        shortOutfit = 'ブラウス (白)';
      } else if (outfit.includes('ブラックのタートルネック')) {
        shortOutfit = 'タートルネック (黒)';
      } else if (outfit.includes('白いタートルネック')) {
        shortOutfit = 'タートルネック (白)';
      } else if (outfit.includes('ベージュのタートルネック')) {
        shortOutfit = 'タートルネック (ベージュ)';
      } else if (outfit.includes('白いシャツ')) {
        shortOutfit = 'シャツ (白)';
      } else if (outfit.includes('ブルーのシャツ')) {
        shortOutfit = 'シャツ (ブルー)';
      } else if (outfit.includes('添付画像と同じ衣装')) {
        shortOutfit = 'カスタム衣装 (画像)';
      } else if (outfit.includes('白いクルーネック') || outfit.includes('シンプルで清潔感のある白いクルーネックTシャツ')) {
        shortOutfit = 'カジュアルTシャツ (白)';
      } else if (outfit.includes('クルーネック') || outfit.includes('Tシャツ')) {
        // Tシャツの色を正確に抽出
        if (outfit.includes('白')) {
          shortOutfit = 'カジュアルTシャツ (白)';
        } else if (outfit.includes('黒') || outfit.includes('ブラック')) {
          shortOutfit = 'カジュアルTシャツ (黒)';
        } else if (outfit.includes('グレー')) {
          shortOutfit = 'カジュアルTシャツ (グレー)';
        } else {
          shortOutfit = outfit;
        }
      } else if (outfit.includes('ブラウス')) {
        // ブラウスの色を正確に抽出
        if (outfit.includes('白')) {
          shortOutfit = 'ブラウス (白)';
        } else if (outfit.includes('ベージュ')) {
          shortOutfit = 'ブラウス (ベージュ)';
        } else {
          shortOutfit = outfit;
        }
      }
      parts.push(`服装: ${shortOutfit}`);
      console.log('✅ 衣装抽出成功:', {
        originalOutfit: outfit,
        shortOutfit
      });
    }
  }
  
  // ポーズの指定を探す（POSE_OPTIONSのpromptプロパティがそのまま追加される）
  const posePatterns = [
    { pattern: /背筋を伸ばし、あごを軽く引き、柔らかな微笑みでカメラ目線。/, label: '正面' },
    { pattern: /背筋を伸ばし、あごを軽く引き、左に少し向きながら柔らかな微笑みでカメラ目線。/, label: 'やや左向き' },
    { pattern: /背筋を伸ばし、あごを軽く引き、右に少し向きながら柔らかな微笑みでカメラ目線。/, label: 'やや右向き' },
    { pattern: /背筋を伸ばし、あごを軽く引き、左を向いたプロフィールポーズで、上品で魅力的な横顔。/, label: '左向き' },
    { pattern: /背筋を伸ばし、あごを軽く引き、右を向いたプロフィールポーズで、上品で魅力的な横顔。/, label: '右向き' },
    { pattern: /肩越しに振り返るような自然なポーズで、少し後ろを振り返りながらもカメラに視線を向ける。首を軽く回転させ、上品で魅力的な見返りポーズ。/, label: '見返り' }
  ];
  
  for (const poseInfo of posePatterns) {
    const match = promptWithoutBackground.match(poseInfo.pattern);
    if (match) {
      parts.push(`ポーズ: ${poseInfo.label}`);
      console.log('✅ ポーズ抽出成功:', {
        label: poseInfo.label,
        pattern: poseInfo.pattern.toString()
      });
      break;
    }
  }
  
  // 重要な部分が見つからない場合は、プロンプトの最初の部分を表示
  if (parts.length === 0) {
    console.log('⚠️ 抽出された部分がありません');
    return prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt;
  }
  
  const result = parts.join(' | ');
  console.log('🎯 最終抽出結果:', {
    partsCount: parts.length,
    result
  });
  
  return result;
};

export const GalleryModal: React.FC<GalleryModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [downloadingImages, setDownloadingImages] = useState<Set<string>>(new Set());
  const [showcasingImages, setShowcasingImages] = useState<Set<string>>(new Set());
  const [removingFromShowcase, setRemovingFromShowcase] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user || !isOpen) return;

    let cancelled = false;
    // 既存表示があるときはスケルトンで消さない
    if (images.length === 0) {
      setLoading(true);
    }

    const loadImages = async () => {
      try {
        const allImages = await loadUserGallery(user.uid, {
          skipFreshDb: true,
          onCached: (cachedImages) => {
            if (cancelled) return;
            setImages(cachedImages as GalleryImage[]);
            setLoading(false);
          },
        });

        if (cancelled) return;
        setImages(allImages as GalleryImage[]);
        console.log('ギャラリー画像を取得:', { total: allImages.length });
      } catch (error) {
        console.error('ギャラリーの読み込みエラー:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadImages();
    return () => {
      cancelled = true;
    };
  }, [user, isOpen]);

  // 画像を削除
  const handleDeleteImage = async (imageId: string) => {
    if (!user) return;

    const imageToDelete = images.find(img => img.id === imageId);
    if (!imageToDelete) return;

    try {
      // Firestoreから削除
      if ((imageToDelete as any).source === 'firestore') {
        const { deleteGeneratedImage } = await import('../services/firebase');
        await deleteGeneratedImage(user.uid, imageId);
      }

      // UIから削除
      const updatedImages = images.filter(img => img.id !== imageId);
      setImages(updatedImages);
      
      // LocalStorageも更新（local画像の場合）
      const localImages = updatedImages.filter((img: any) => img.source === 'local');
      localStorage.setItem(`gallery_${user.uid}`, JSON.stringify(localImages));
      invalidateGalleryCache(user.uid);
    } catch (error) {
      console.error('画像削除エラー:', error);
      alert('画像の削除に失敗しました');
    }
  };

  // 複数選択削除
  const handleDeleteSelected = async () => {
    if (!user || selectedImages.size === 0) return;

    try {
      // Firestore画像を削除
      const { deleteGeneratedImage } = await import('../services/firebase');
      const deletePromises = Array.from(selectedImages).map(async (imageId) => {
        const imageToDelete = images.find(img => img.id === imageId);
        if (imageToDelete && (imageToDelete as any).source === 'firestore') {
          await deleteGeneratedImage(user.uid, imageId);
        }
      });
      await Promise.all(deletePromises);

      // UIから削除
      const updatedImages = images.filter(img => !selectedImages.has(img.id));
      setImages(updatedImages);
      setSelectedImages(new Set());
      
      // LocalStorageも更新
      const localImages = updatedImages.filter((img: any) => img.source === 'local');
      localStorage.setItem(`gallery_${user.uid}`, JSON.stringify(localImages));
      invalidateGalleryCache(user.uid);
    } catch (error) {
      console.error('画像削除エラー:', error);
      alert('画像の削除に失敗しました');
    }
  };

  // 画像を選択/選択解除
  const toggleImageSelection = (imageId: string) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };

  // 画像をダウンロード（改善版）
  const downloadImage = async (imageUrl: string, filename: string, imageId: string) => {
    if (downloadingImages.has(imageId)) return;
    
    setDownloadingImages(prev => new Set(prev).add(imageId));
    
    try {
      console.log('💾 Gallery - Starting download:', {
        imageId,
        filename,
        imageUrlPreview: imageUrl.substring(0, 100) + '...'
      });
      
      // Firebase Storage URLの場合はfetchを使用
      if (imageUrl.startsWith('http')) {
        await downloadImageWithFetch(imageUrl, filename);
      } else {
        // base64データの場合は直接ダウンロード
        await downloadImageDirect(imageUrl, filename);
      }
      
      console.log('✅ Gallery - Download completed:', imageId);
    } catch (error) {
      console.error('❌ Gallery - Download failed:', error);
      // フォールバック: 直接ダウンロードを試行
      try {
        await downloadImageDirect(imageUrl, filename);
        console.log('✅ Gallery - Fallback download completed:', imageId);
      } catch (fallbackError) {
        console.error('❌ Gallery - Fallback download also failed:', fallbackError);
        alert('ダウンロードに失敗しました。ブラウザの設定を確認してください。');
      }
    } finally {
      setDownloadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };

  // fetchを使用したダウンロード
  const downloadImageWithFetch = async (imageUrl: string, filename: string) => {
    console.log('🌐 Gallery - Fetching image for download:', imageUrl.substring(0, 100) + '...');
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    console.log('📦 Gallery - Image blob created:', blob.size, 'bytes');
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    setTimeout(() => {
      link.click();
      console.log('✅ Gallery - Fetch-based download initiated');
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    }, 50);
  };

  // 直接ダウンロード
  const downloadImageDirect = async (imageUrl: string, filename: string) => {
    return new Promise<void>((resolve, reject) => {
      try {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = filename;
        link.style.display = 'none';
        link.addEventListener('click', () => {
          console.log('✅ Gallery - Direct download link clicked');
        });
        document.body.appendChild(link);
        
        setTimeout(() => {
          link.click();
          console.log('✅ Gallery - Direct download initiated');
          setTimeout(() => {
            document.body.removeChild(link);
            resolve();
          }, 100);
        }, 50);
      } catch (error) {
        reject(error);
      }
    });
  };

  // ショーケースに追加（楽観的UI: 押した瞬間に完了表示、裏で書き込み）
  const addImageToShowcase = async (image: GalleryImage) => {
    if (showcasingImages.has(image.id) || image.showcaseId) return;

    if (!image.generatedImage || !/^https?:\/\//i.test(image.generatedImage)) {
      alert('この画像はショーケースに追加できません。有料プランで保存された画像をお使いください。');
      return;
    }

    const pendingId = `pending_${image.id}`;
    // 先にUIを「追加済み」に切り替え（待ち時間を感じさせない）
    setImages(prev =>
      prev.map(img => (img.id === image.id ? { ...img, showcaseId: pendingId } : img)),
    );

    const safePrompt = image.prompt && image.prompt.trim() !== ''
      ? image.prompt
      : '生成された宣材写真';

    const lightSettings = {
      mode: image.settings?.mode,
      background: image.settings?.backgroundOption || image.settings?.backgroundId || image.background,
      outfit: image.settings?.selectedOutfitId || image.settings?.dressId || image.outfit,
      pose: image.settings?.selectedPoseId || image.settings?.poseId || image.pose,
      backgroundOption: image.settings?.backgroundOption,
      selectedOutfitId: image.settings?.selectedOutfitId,
      selectedPoseId: image.settings?.selectedPoseId,
      backgroundId: image.settings?.backgroundId,
      dressId: image.settings?.dressId,
      poseId: image.settings?.poseId,
      sleeveId: image.settings?.sleeveId,
      itemId: image.settings?.itemId,
      // 「このスタイルで」で入力欄に戻す自由記述
      userPrompt: image.settings?.userPrompt,
      customBackground: image.settings?.customBackground,
      customOutfitText: image.settings?.customOutfitText,
      customBackgroundText: image.settings?.customBackgroundText,
      customDressText: image.settings?.customDressText,
      customSleeveText: image.settings?.customSleeveText,
      customItemText: image.settings?.customItemText,
    };

    try {
      const showcaseId = await addToShowcase(
        image.generatedImage,
        safePrompt,
        lightSettings,
        image.id,
      );

      setImages(prev =>
        prev.map(img => (img.id === image.id ? { ...img, showcaseId } : img)),
      );
      if (user?.uid) {
        invalidateGalleryCache(user.uid);
      }
    } catch (error) {
      // 失敗時だけ元に戻す
      setImages(prev =>
        prev.map(img =>
          img.id === image.id && img.showcaseId === pendingId
            ? { ...img, showcaseId: undefined }
            : img,
        ),
      );

      console.error('❌ Gallery - Showcase addition failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('認証')) {
        alert('ログインが必要です。再度ログインしてお試しください。');
      } else if (errorMessage.includes('権限') || errorMessage.includes('permission') || (error as any)?.code === 'permission-denied') {
        alert('ショーケースへの追加権限がありません。');
      } else if (errorMessage.includes('タイムアウト')) {
        alert(errorMessage);
      } else {
        alert(`ショーケースへの追加に失敗しました: ${errorMessage}`);
      }
    }
  };

  // ショーケースから削除
  const removeImageFromShowcase = async (image: GalleryImage) => {
    if (!image.showcaseId || image.showcaseId.startsWith('pending_') || removingFromShowcase.has(image.id)) return;
    
    setRemovingFromShowcase(prev => {
      const newSet = new Set(prev);
      newSet.add(image.id);
      return newSet;
    });
    
    try {
      console.log('🗑️ Gallery - Removing from showcase:', {
        imageId: image.id,
        showcaseId: image.showcaseId
      });
      
      // 1. showcaseIdを使ってショーケースから削除
      await removeFromShowcase(image.showcaseId!);
      
      // 2. ギャラリーのshowcaseIdをクリア（未定義にする）
      setImages(prevImages => 
        prevImages.map(img => 
          img.id === image.id 
            ? { ...img, showcaseId: undefined }
            : img
        )
      );
      
      // 3. LocalStorageからもshowcaseIdを削除（次回読み込み時に反映されるため）
      // ただし、次回読み込み時にFirestoreのマッチング結果で上書きされるので必須ではない
      if ((image as any).source === 'local') {
        const savedImages = localStorage.getItem(`gallery_${user?.uid}`);
        if (savedImages) {
          const parsed = JSON.parse(savedImages);
          const updated = parsed.map((img: any) => 
            img.id === image.id ? { ...img, showcaseId: undefined } : img
          );
          localStorage.setItem(`gallery_${user?.uid}`, JSON.stringify(updated));
        }
      }
      invalidateGalleryCache(user.uid);
      
      console.log('✅ Gallery - Removed from showcase:', image.showcaseId);
      alert('ショーケースから削除しました！');
    } catch (error) {
      console.error('❌ Gallery - Showcase removal failed:', {
        error,
        imageId: image.id,
        showcaseId: image.showcaseId
      });
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('認証')) {
        alert('ログインが必要です。再度ログインしてお試しください。');
      } else if (errorMessage.includes('権限') || errorMessage.includes('permission')) {
        alert('ショーケースからの削除権限がありません。');
      } else {
        alert(`ショーケースからの削除に失敗しました: ${errorMessage}`);
      }
    } finally {
      setRemovingFromShowcase(prev => {
        const newSet = new Set(prev);
        newSet.delete(image.id);
        return newSet;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">ギャラリー</h2>
            <p className="text-slate-600 text-sm mt-1">
              {images.length}枚の画像
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedImages.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                <span>選択した画像を削除</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
              aria-label="閉じる"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* ボタン説明 */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-center space-x-6 text-sm text-slate-600">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                <DownloadIcon className="w-3 h-3 text-white" />
              </div>
              <span>ダウンロード</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                <ShowcaseIcon className="w-3 h-3 text-white" />
              </div>
              <span>ショーケースに追加</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                <BroomIcon className="w-3 h-3 text-white" />
              </div>
              <span>ショーケースから削除</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                <TrashIcon className="w-3 h-3 text-white" />
              </div>
              <span>画像を削除</span>
            </div>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && images.length === 0 ? (
            <div>
              <p className="text-center text-sm text-slate-500 mb-4">読み込み中...</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-slate-200 animate-pulse" />
                    <div className="p-4 space-y-2">
                      <div className="h-3 w-2/3 rounded-full bg-slate-200 animate-pulse" />
                      <div className="h-3 w-1/3 rounded-full bg-slate-200 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">まだ画像がありません</h3>
              <p className="text-slate-600">宣材写真を生成すると、ここに表示されます。</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image) => (
                <div key={image.id} className="bg-slate-50 rounded-lg overflow-hidden">
                  <div className="aspect-square relative">
                    <img
                      src={image.generatedImage}
                      alt="Generated headshot"
                      className="w-full h-full object-cover"
                      onLoad={() => console.log('✅ Gallery image loaded:', image.id)}
                      onError={(e) => {
                        console.error('❌ Gallery image failed to load:', {
                          id: image.id,
                          src: image.generatedImage,
                          error: e
                        });
                      }}
                    />
                    {/* 選択チェックボックス */}
                    <div className="absolute top-2 left-2">
                      <button
                        onClick={() => toggleImageSelection(image.id)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          selectedImages.has(image.id)
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-slate-300 hover:border-blue-500'
                        }`}
                      >
                        {selectedImages.has(image.id) && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {/* アクションボタン */}
                    <div className="absolute top-2 right-2 flex space-x-2">
                      <button
                        onClick={() => {
                          const filename = image.generatedImage.startsWith('http') 
                            ? `headshot-${image.id}.jpg` 
                            : `headshot-${image.id}.png`;
                          downloadImage(image.generatedImage, filename, image.id);
                        }}
                        className={`p-2 rounded-full transition-colors ${
                          downloadingImages.has(image.id)
                            ? 'bg-blue-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        aria-label={downloadingImages.has(image.id) ? "ダウンロード中..." : "ダウンロード"}
                        disabled={downloadingImages.has(image.id)}
                      >
                        {downloadingImages.has(image.id) ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <DownloadIcon className="w-4 h-4" />
                        )}
                      </button>
                      {image.showcaseId ? (
                        <button
                          onClick={() => removeImageFromShowcase(image)}
                          className={`p-2 rounded-full transition-colors ${
                            removingFromShowcase.has(image.id) || image.showcaseId.startsWith('pending_')
                              ? 'bg-red-400 text-white cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700'
                          }`}
                          aria-label={
                            image.showcaseId.startsWith('pending_')
                              ? 'ショーケース追加中...'
                              : removingFromShowcase.has(image.id)
                                ? 'ショーケース削除中...'
                                : 'ショーケースから削除'
                          }
                          disabled={removingFromShowcase.has(image.id) || image.showcaseId.startsWith('pending_')}
                        >
                          {removingFromShowcase.has(image.id) || image.showcaseId.startsWith('pending_') ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <BroomIcon className="w-4 h-4" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => addImageToShowcase(image)}
                          className={`p-2 rounded-full transition-colors ${
                            showcasingImages.has(image.id)
                              ? 'bg-purple-400 text-white cursor-not-allowed'
                              : 'bg-purple-600 text-white hover:bg-purple-700'
                          }`}
                          aria-label={showcasingImages.has(image.id) ? "ショーケース追加中..." : "ショーケースに追加"}
                          disabled={showcasingImages.has(image.id)}
                        >
                          {showcasingImages.has(image.id) ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <ShowcaseIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteImage(image.id)}
                        className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                        aria-label="削除"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-slate-600">
                      {image.createdAt.toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    {image.prompt && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-3">
                        {(() => {
                          const extracted = extractImportantPromptParts(image.prompt);
                          console.log('🔍 プロンプト抽出デバッグ:', {
                            originalPrompt: image.prompt.substring(0, 500) + '...',
                            extractedParts: extracted,
                            imageId: image.id,
                            backgroundMatches: image.prompt.match(/背景は[：:]/g),
                            backgroundCount: (image.prompt.match(/背景は[：:]/g) || []).length
                          });
                          return extracted;
                        })()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
